import Handsontable from 'handsontable';
import classNames from 'classnames';
import { Action } from 'redux';

import {
  addDataGridSeriesAction,
  clearColumnAction,
  deleteDataGridSeriesAction,
  editDataGridAction as editChartDataGridAction,
  sortDataGridAction
} from 'pages/ChartEditorPage/actions/chartEditor';
import store from 'redux/store';
import { UnknownParamsFunction } from 'shared/types/commonPropTypes';
import { setDataAction as setTableDataAction } from '../../../pages/TableEditorPage/actions/tableEditor';
import { setDataAction as setChartDataAction } from '../../../pages/ChartEditorPage/actions/chartEditor';

export type CoordProps = {
  col: number;
  row: number;
};

export const setDataMap: { [key: string]: UnknownParamsFunction } = {
  chart: setChartDataAction,
  table: setTableDataAction
};

const isMac = window.navigator.platform.indexOf('Mac') > -1;
export const hotContextMenuConfig: Handsontable.plugins.ContextMenu.Settings = {
  items: {
    sort_asc: {
      name: '<div class="context-menu-item"><img src="/static/icons/ascending.svg"/><span>Sort ascending</span></div>',
      callback(this, key, selection) {
        const { type } = store.getState().layoutEditorPage;
        const index = selection[0].start.col;
        store.dispatch(sortDataGridAction({ type, column: index, sortOrder: 'asc' }));
      },
      disabled(this) {
        const { isMap } = store.getState().chartEditorPage;
        const selection = this.getSelected();
        if (selection) return (isMap && selection[0][1] === 0) || selection[0][0] >= 0;
      }
    },
    sort_desc: {
      name: '<div class="context-menu-item"><img src="/static/icons/descending.svg"/><span>Sort descending</span></div>',
      callback(this, key, selection) {
        const { type } = store.getState().layoutEditorPage;
        const index = selection[0].start.col;
        store.dispatch(sortDataGridAction({ type, column: index, sortOrder: 'desc' }));
      },
      disabled(this) {
        const { isMap } = store.getState().chartEditorPage;
        const selection = this.getSelected();
        if (selection) return (isMap && selection[0][1] === 0) || selection[0][0] >= 0;
      }
    },
    row_above: {
      name: '<div class="context-menu-item"><img src="/static/icons/add.svg"/><span>Add row above</span></div>',
      callback(this, key, selection) {
        const index = selection[0].start.row;
        const { type } = store.getState().layoutEditorPage;
        this.alter('insert_row', index);
        store.dispatch(addDataGridSeriesAction({ type, index, amount: 1, source: 'ContextMenu.rowAbove' }));
      },
      disabled(this) {
        const { isMap } = store.getState().chartEditorPage;
        const selection = this.getSelected();
        return isMap || (selection && selection[0][0] === -1);
      }
    },
    row_below: {
      name: '<div class="context-menu-item"><img src="/static/icons/add.svg"/><span>Add row below</span></div>',
      callback(this, key, selection) {
        const { type } = store.getState().layoutEditorPage;
        const index = selection[0].start.row + 1;
        this.alter('insert_row', index);
        store.dispatch(addDataGridSeriesAction({ type, index, amount: 1, source: 'ContextMenu.rowBelow' }));
      },
      disabled(this) {
        const { isMap } = store.getState().chartEditorPage;
        const selection = this.getSelected();
        return isMap || (selection && selection[0][0] === -1);
      }
    },
    separator1: {
      name: '<div class="context-menu-separator"></div>'
    },
    remove_col: {
      name: '<div class="context-menu-item"><img src="/static/icons/delete.svg"/><span>Delete column</span></div>',
      callback(this, key, selection) {
        const { type } = store.getState().layoutEditorPage;
        const startIndex = selection[0].start.col;
        const endIndex = selection[0].end.col;
        this.alter('remove_col', startIndex);
        store.dispatch(
          deleteDataGridSeriesAction({
            type,
            index: startIndex,
            amount: endIndex - startIndex + 1,
            source: 'ContextMenu.removeColumn'
          })
        );
      },
      disabled(this) {
        const { isMap } = store.getState().chartEditorPage;
        return isMap;
      }
    },
    clear_column: {
      name: '<div class="context-menu-item"><img src="/static/icons/clear.svg"/><span>Clear column</span></div>',
      callback(this, key, selection) {
        const { type } = store.getState().layoutEditorPage;
        const startIndex = selection[0].start.col;
        const endIndex = selection[0].end.col;
        for (let i = startIndex; i <= endIndex; i++) {
          this.alter('remove_col', startIndex);
        }

        store.dispatch(
          clearColumnAction({
            type,
            index: startIndex,
            amount: endIndex - startIndex + 1
          })
        );
      },
      disabled(this) {
        const { isMap } = store.getState().chartEditorPage;
        const selection = this.getSelected();
        if (selection) return isMap && selection[0][1] === 0;
      }
    },
    remove_row: {
      name: '<div class="context-menu-item"><img src="/static/icons/delete.svg"/><span>Delete row</span></div>',
      callback(this, key, selection) {
        const { type } = store.getState().layoutEditorPage;
        const startIndex = selection[0].start.row;
        const endIndex = selection[0].end.row;
        this.alter('remove_row', startIndex);

        store.dispatch(
          deleteDataGridSeriesAction({
            type,
            index: startIndex,
            amount: endIndex - startIndex + 1,
            source: 'ContextMenu.removeRow'
          })
        );
      },
      disabled(this) {
        const selection = this.getSelected();
        const { isMap } = store.getState().chartEditorPage;
        return isMap || (selection && selection[0][0] === -1);
      }
    },
    clear_row: {
      name: '<div class="context-menu-item"><img src="/static/icons/clear.svg"/><span>Clear row</span></div>',
      callback(this, key, selection) {
        const { type } = store.getState().layoutEditorPage;
        const startIndex = selection[0].start.row;
        const endIndex = selection[0].end.row;
        this.alter('remove_row', startIndex);
        store.dispatch(
          deleteDataGridSeriesAction({
            type,
            index: startIndex,
            amount: endIndex - startIndex + 1,
            source: 'ContextMenu.removeRow'
          })
        );
      },
      disabled(this) {
        const selection = this.getSelected();
        const { isMap } = store.getState().chartEditorPage;
        if (selection) return isMap || selection[0][1] >= 0;
      }
    },
    separator2: {
      name: '<div class="context-menu-separator"></div>'
    },
    col_left: {
      name: '<div class="context-menu-item"><img src="/static/icons/add.svg"/><span>Add column left</span></div>',
      callback(this, key, selection) {
        const index = selection[0].start.col;
        const { type } = store.getState().layoutEditorPage;
        this.alter('insert_col', index);
        store.dispatch(addDataGridSeriesAction({ type, index, amount: 1, source: 'ContextMenu.columnLeft' }));
      },
      disabled(this) {
        const selection = this.getSelected();
        if (selection) return selection[0][1] === 0;
      }
    },
    col_right: {
      name: '<div class="context-menu-item"><img src="/static/icons/add.svg"/><span>Add column right</span></div>',
      callback(this, key, selection) {
        const { type } = store.getState().layoutEditorPage;
        const index = selection[0].end.col;
        this.alter('insert_col', index + 1);
        store.dispatch(
          addDataGridSeriesAction({ type, index: index + 1, amount: 1, source: 'ContextMenu.columnRight' })
        );
      }
    },
    separator3: {
      name: '<div class="context-menu-separator"></div>'
    },
    cut: {
      name: () => {
        return `<div class="context-menu-item cursor-not-allowed pointer-events-none"><img src="/static/icons/cut.svg"/><span>Cut</span>
        <span class="help-text ml-auto mr-1 text-ev-dark-purple">Use ${
          isMac ? '<i class="fa-solid fa-command"></i>' : 'Ctrl'
        }+X</span></div>`;
      }
    },
    copy: {
      name: () => {
        return `<div class="context-menu-item cursor-not-allowed pointer-events-none"><img src="/static/icons/copy.svg"/><span>Copy</span>
        <span class="help-text ml-auto mr-1 text-ev-dark-purple">Use ${
          isMac ? '<i class="fa-solid fa-command"></i>' : 'Ctrl'
        }+C</span></div>`;
      }
    },
    paste: {
      name: () => {
        return `<div class="context-menu-item cursor-not-allowed pointer-events-none"><img src="/static/icons/paste.svg"/><span>Paste</span>
        <span class="help-text ml-auto mr-1 text-ev-dark-purple">Use ${
          isMac ? '<i class="fa-solid fa-command"></i>' : 'Ctrl'
        }+V</span></div>`;
      }
    },
    separator4: {
      name: '<div class="context-menu-separator"></div>'
    },
    undo: {
      name: '<div class="context-menu-item"><img src="/static/icons/undo.svg"/><span>Undo</span></div>',
      disabled(this) {
        const selection = this.getSelected();
        if (selection) return selection[0][0] < 0;
      }
    },
    redo: {
      name: '<div class="context-menu-item"><img src="/static/icons/redo.svg"/><span>Redo</span></div>',
      disabled(this) {
        const selection = this.getSelected();
        if (selection) return selection[0][0] < 0;
      }
    }
  }
};

export const editData: Record<
  string,
  (data: { changes: Handsontable.CellChange[]; seriesAssigns: any[]; isTable?: boolean }) => Action
> = {
  chart: editChartDataGridAction,
  table: editChartDataGridAction
};

export const cellsConfig = (
  row: number,
  col: number,
  isMap: boolean,
  categories: number[],
  series: number[],
  isPointMap: boolean,
  activeColumns?: number[]
) => {
  const active = activeColumns?.includes(col);
  const cellsClasses = classNames({
    'font-bold': row === 0,
    'bg-ev-baby-blue-3': series.includes(col) && !active, // Default highlighting of series
    'bg-ev-baby-blue': (!isMap || isPointMap) && !active && categories.includes(col), // default categories highlighting (Charts & PointMap)
    'bg-ev-grey': isMap && !isPointMap && !active && categories.includes(col), // default categories highlighting (Maps excl PointMap)
    'bg-ev-baby-blue-2': active // Highlighting when hovered
  });
  return {
    readOnly: isMap && !isPointMap && col === 0,
    className: cellsClasses
  };
};

export const afterGetColHeader = (col: number, TH: HTMLElement) => {
  if (TH.querySelector('button') || col === -1) {
    return;
  }

  const btn = document.createElement('button');
  btn.innerHTML = '<i class="fa fa-chevron-down cursor-pointer"/>';

  TH.firstChild?.appendChild(btn);
};

const getSelectedSection = (hot: Handsontable) => {
  const selected_array = hot.getSelected();
  return selected_array ? selected_array[0] : null;
};

export const beforeOnCellMouseDown = (e: MouseEvent, coords: CoordProps, hot: Handsontable) => {
  if (coords.row >= 0) {
    return;
  }

  const tagName = (e.target as HTMLElement).tagName;
  if (tagName === 'I' || tagName === 'BUTTON') {
    // Show right click dropdown
    if (!hot) return;
    let selected: number[] | null = getSelectedSection(hot);

    if (!selected || (selected && selected[0] > 0 && selected[2] > 0)) {
      hot.selectColumns(coords.col, coords.col);
      selected = getSelectedSection(hot);
    } else {
      const left = Math.min(selected[1], selected[3]);
      const right = Math.max(selected[1], selected[3]);
      const isSelected = coords.col >= left && coords.col <= right;
      if (!isSelected) {
        hot.selectColumns(coords.col, coords.col);
      }
    }

    const contextMenu = hot.getPlugin('ContextMenu') as Handsontable.plugins.ContextMenu;
    contextMenu.close();
    contextMenu.open(e);
    e.stopImmediatePropagation();
  }
};
